import { NetworkErrorBoundary, useSubscription } from '@rest-hooks/react'
import { AppProvider, Frame } from '@shopify/polaris'
import enTranslations from '@shopify/polaris/locales/en.json'
import React, { Suspense, useCallback, useState } from 'react'
import { Link, Navigate, Route, Routes } from 'react-router-dom'

import PhilipsShield from '../assets/images/PhilipsShield.svg'
import LoadingPage from '../components/LoadingPage'
import UnsavedChangesModal from '../components/UnsavedChangesModal'
import { ROUTES } from '../constants/navigation'
import { CheckLogin } from '../resources/auth'
import { useAuthInfo, useCheckLogin } from '../utils/auth'

function PolarisLink({ children, url = '', external, ...rest }) {
  if (external) {
    return <a href={url} {...rest}>{children}</a>
  }
  return <Link to={url} {...rest}>{children}</Link>
}

const TopBar = React.lazy(() => import('./Header'))
const SideNav = React.lazy(() => import('./SideNav'))
const Landing = React.lazy(() => import('../features/Landing/Landing'))
const ErrorPage = React.lazy(() => import('../features/Error/ErrorPage'))

const LoggedInRoutes = () => {
  const checkLoginEndpoint = React.useMemo(() => CheckLogin.checkLoginShape(), []);
  useSubscription(checkLoginEndpoint, {});
  const { role } = useAuthInfo()

  // Split routes by permission
  const { routes, noPermission } = Object.values(ROUTES).reduce(
    (acc, curr) => {
      const hasPermission = (curr.roles ?? []).length ? curr.roles.some(r => r === role) : true
      if (hasPermission) {
        acc.routes.push(curr)
      } else {
        acc.noPermission.push(curr)
      }
      return acc
    },
    { routes: [], noPermission: [] }
  )

  // TODO -> no theme & app provider bug starts here
  return (
    <AppProvider i18n={enTranslations} linkComponent={PolarisLink}>
      <Frame logo={{
        width: 32,
        topBarSource: PhilipsShield,
        url: '/',
        accessibilityLabel: 'Philips'
      }} topBar={<TopBar />} navigation={<SideNav />}>
        <Suspense fallback={<LoadingPage />}>
          <Routes>
            {routes.map(({ icon, label, component: Component, path, ...rest }) => (
              <Route
                key={path}
                path={path}
                element={
                  <NetworkErrorBoundary fallbackComponent={err => <ErrorPage error={err.error} />}>
                    <Component />
                  </NetworkErrorBoundary>
                }
                {...rest}
              />
            ))}
            {noPermission.map(({ path }) => (
              <Route key={path} path={path} element={<ErrorPage error={{ status: 403 }} />} />
            ))}
            {/* Redirect root and index.html to home */}
            <Route path='/' element={<Navigate to='/home' replace />} />
            <Route path='/index.html' element={<Navigate to='/home' replace />} />
            {/* Catch-all 404 */}
            <Route path='*' element={<ErrorPage error={{ status: 404 }} />} />
          </Routes>
        </Suspense>
      </Frame>
    </AppProvider>
  )
}

const LoggedOutRoutes = () => (
  <Suspense fallback={<LoadingPage />}>
    <Routes>
      <Route path='/' element={<Landing />} />
      <Route path='*' element={<Navigate to='/' replace />} />
    </Routes>
  </Suspense>
)

const AppRoutes = () => {
  const loggedIn = useCheckLogin()
  const [callback, setCallback] = useState(null)

  const handleAction = useCallback(
    confirmed => {
      callback(confirmed)
      setCallback(null)
    },
    [callback]
  )

  const handleGetConfirmation = (_, callback) => setCallback(() => callback)
  const handleConfirm = () => handleAction(true)
  const handleCancel = () => handleAction(false)

  // NOTE: getUserConfirmation prop was removed in react-router-dom v6.
  // Pending: replace with a custom navigation blocker if unsaved changes feature is required.
  return (
    <>
      {loggedIn ? <LoggedInRoutes /> : <LoggedOutRoutes />}
      {/* <LoggedOutRoutes /> */}
      <UnsavedChangesModal open={callback !== null} confirm={handleConfirm} cancel={handleCancel} />
    </>
  )
}

export default AppRoutes
